// Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
// SPDX-License-Identifier: MIT-0
import React from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import "@cloudscape-design/global-styles/index.css";
import {
  CognitoAuthProvider,
  useAuth,
  type CognitoOAuthConfig,
} from "./auth/CognitoAuthProvider";
import LoginPage from "./auth/LoginPage";
import EnrollmentWizard from "./enrollment/EnrollmentWizard";
import { LocalAgentClient } from "./services/localAgentClient";
import { BackendClient } from "./services/backendClient";
import { appConfig } from "./config";

interface AppProps {
  oauthConfig: CognitoOAuthConfig;
}

const localAgentClient = new LocalAgentClient(appConfig.localAgentUrl);
const backendClient = new BackendClient(appConfig.apiUrl);

/** Route guard — redirects unauthenticated users to the landing page */
function RequireAuth({ children }: { children: React.ReactElement }): React.ReactElement {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return <div />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/" replace />;
  }

  return children;
}

export default function App({ oauthConfig }: AppProps): React.ReactElement {
  return (
    <BrowserRouter>
      <CognitoAuthProvider oauthConfig={oauthConfig}>
        <Routes>
          <Route path="/" element={<LoginPage />} />
          <Route
            path="/enroll"
            element={
              <RequireAuth>
                <EnrollmentWizard
                  localAgentClient={localAgentClient}
                  backendClient={backendClient}
                />
              </RequireAuth>
            }
          />
        </Routes>
      </CognitoAuthProvider>
    </BrowserRouter>
  );
}
